/* eslint-disable no-console */
import {CheckOutlined, ExclamationCircleOutlined} from '@ant-design/icons';
import {Collapse, Form, Button, Tooltip} from 'antd';
import {useForm} from 'antd/es/form/Form';
import React, {Fragment, useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

import Loader from '../../../components/Loader/Loader';
// import {Routes} from '../../../constants/Routes';
import {APITypeEnum} from '../../../services/interface/tableAPIType';
import esgAPI from '../../../services/modules/ESG/ESGAPI';
import pillarsAPI from '../../../services/modules/ESG/pillarsAPI';
import cityJSON from '../../../services/modules/JSON/cityJSON';
import country from '../../../services/modules/JSON/country';
import statesJSON from '../../../services/modules/JSON/statesJSON';
import {replaceCurrentAndPrevious} from '../../../shared/checkSelectedYear';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import useUserStore from '../../../store/userStore/userStore';
import {checkCurrentYearOrBelow} from '../../../utils/commonFunctions';
import showMessage from '../../../utils/showMessage';
import SubmitDisabledContext from '../constants/SubmitDisabledContext';
import {IField, inputType} from '../interface/inputType';

import QuestionnaireSwitch from './QuestionnaireSwitch';

const Questionnaire: React.FC<{
  tabId: string;
  items: any[];
  accordion?: boolean;
  nextTabId: string;
  setDefaultKey: React.Dispatch<React.SetStateAction<string>>;
  APIRoute: string;
  setIsSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  principleTitle: string;
}> = ({
  tabId,
  items,
  accordion = true,
  nextTabId,
  setDefaultKey,
  APIRoute,
  setIsSubmitted,
  principleTitle
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedYear = checkCurrentYearOrBelow(
    searchParams.get('selectedYear')
  );
  const [isLoading, setIsLoading] = useState(true);
  const [onlySubmit, setOnlySubmit] = useState(true);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<any>();
  const [validationData, setValidationData] = useState<any>();
  const companyId = useCompanyStore.getState().companyId;
  const userDetail = useUserStore.getState().userDetails;
  // const navigate = useNavigate();

  const [form] = useForm();
  useEffect(() => {
    if (selectedYear) {
      searchParams.set('selectedYear', selectedYear);
      setSearchParams(searchParams);
      Promise.all([
        country(),
        statesJSON(),
        cityJSON(),
        esgAPI(
          APITypeEnum.GET_ALL,
          APIRoute,
          undefined,
          tabId,
          selectedYear ?? ''
        )
          .then((res) => {
            setFormData(res);
            setValidationData(res?.validationData ?? null);
          })
          .catch(() => {
            form.resetFields();
            setFormData({});
          })
          .finally(() => {
            setIsLoading(false);
          })
      ])
        .catch(() => {})
        .finally(() => {});
    }
  }, [tabId, selectedYear]);

  const handleSubmit = (values: any) => {
    const updatedValues = updateUndefinedFieldsToNull(values);

    const data = {
      type: tabId,
      data: {
        ...updatedValues
      },
      year: selectedYear,
      company: companyId,
      createdBy: userDetail?._id,
      updatedBy: userDetail?._id
    };
    setIsSubmitting(true);
    esgAPI(
      formData?._id ? APITypeEnum.PUT : APITypeEnum.POST_NEW,
      APIRoute,
      data,
      '',
      '',
      formData?._id ?? ''
    )
      .then((res) => {
        pillarsAPI(selectedYear ?? '', true)
          .then(() => {})
          .catch(() => {})
          .finally(() => {
            showMessage(res?.data);
            setIsSubmitted(true);
            setIsSubmitting(false);
            setIsSubmitDisabled(true);
            if (!onlySubmit) {
              handleSaveAndNext();
            }
          });
        setFormData(res.data.data);
      })
      .catch(() => {})
      .finally(() => {});
  };

  const updateUndefinedFieldsToNull = (newObj: any) => {
    for (const key in newObj) {
      if (newObj.hasOwnProperty(key) && newObj[key] === undefined) {
        newObj[key] = null;
      }
    }

    return newObj;
  };

  const handleSaveAndNext = () => {
    setDefaultKey(nextTabId);
    window.scrollTo(0, 0);
  };

  const tooltipCSS = {
    width: 'auto',
    maxWidth: '500px',
    height: 'auto',
    padding: '12px'
  };

  return isLoading ? (
    <Loader />
  ) : (
    <>
      {principleTitle && (
        <div
          style={{
            font: 'normal normal 600 18px/17px Montserrat',
            color: '#000000',
            margin: '2.5rem 0'
          }}
        >
          {principleTitle}
        </div>
      )}
      <Form
        layout="horizontal"
        onFinish={handleSubmit}
        initialValues={formData?.data}
        // onKeyDown={(e) => {
        //   e.key === 'Enter' && e.preventDefault();
        // }}
        onValuesChange={() => {
          setIsSubmitDisabled(false);
        }}
        // disabled={
        //   hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        // }
        id="moduleForm"
        form={form}
        className="moduleForm"
      >
        {accordion ? (
          <Collapse
            defaultActiveKey={[items?.[0]?.key ?? '1']}
            accordion
            destroyInactivePanel={false}
          >
            {items.map((item) => {
              const color = Math.floor(Math.random() * 2);
              const replaceLabel = replaceCurrentAndPrevious(
                item?.label,
                selectedYear ?? ''
              );

              return (
                <Collapse.Panel
                  forceRender={true}
                  header={
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <div>
                        {item?.kpiId && (
                          <div
                            style={{
                              font: 'normal 600 14px/22px Montserrat'
                            }}
                          >
                            {`#${item?.kpiId}`}
                          </div>
                        )}
                        <div
                          style={{
                            font: item?.kpiId
                              ? 'normal 500 14px/22px Montserrat'
                              : 'normal 600 14px/22px Montserrat'
                          }}
                        >
                          {replaceLabel}
                        </div>
                      </div>

                      <CheckOutlined
                        style={{
                          color: color ? '#22AF5E' : '#D9D9D9',
                          fontSize: '16px'
                        }}
                      />
                    </div>
                  }
                  key={item.key}
                >
                  {item.items.map((item: any, index: number) => {
                    return (
                      <div key={index} style={{padding: 5}}>
                        <div>
                          {item?.headings?.map((head: any, index: number) => {
                            const replaceHeading = replaceCurrentAndPrevious(
                              head?.heading,
                              selectedYear ?? ''
                            );
                            switch (head.headingType) {
                              case inputType.HEADING_5:
                                return <h5 key={index}>{replaceHeading}</h5>;
                              case inputType.HEADING_4:
                                return <h4 key={index}>{replaceHeading}</h4>;
                              case inputType.HEADING_3:
                                return <h3 key={index}>{replaceHeading}</h3>;
                              case inputType.HEADING_2:
                                return <h2 key={index}>{replaceHeading}</h2>;
                              case inputType.HEADING_1:
                                return <h1 key={index}>{replaceHeading}</h1>;
                              default:
                                return <h2 key={index}>{replaceHeading}</h2>;
                            }
                          })}
                        </div>
                        <div>
                          {item.question === '' ? (
                            <></>
                          ) : item?.tooltip ? (
                            <>
                              <b>{item.question}</b>
                              <Tooltip
                                title={item.tooltip}
                                autoAdjustOverflow={true}
                                arrow={false}
                                overlayStyle={tooltipCSS}
                              >
                                <ExclamationCircleOutlined className="iButtonIcon" />
                              </Tooltip>
                            </>
                          ) : (
                            <b>{item.question}</b>
                          )}
                        </div>
                        <div style={{marginTop: 7}}>
                          <SubmitDisabledContext.Provider
                            value={setIsSubmitDisabled}
                          >
                            {item.field.map(
                              (field: IField, fieldIndex: number) => {
                                // eslint-disable-next-line react/jsx-key
                                return (
                                  <QuestionnaireSwitch
                                    field={{
                                      ...field,
                                      fieldLabel: replaceCurrentAndPrevious(
                                        field.fieldLabel,
                                        selectedYear ?? ''
                                      )
                                    }}
                                    key={fieldIndex}
                                  />
                                );
                              }
                            )}
                          </SubmitDisabledContext.Provider>
                        </div>
                      </div>
                    );
                  })}
                </Collapse.Panel>
              );
            })}
          </Collapse>
        ) : (
          <>
            {items.map((mainItem, id) => {
              return (
                <Fragment key={id}>
                  {mainItem.items.map((item: any, index: number) => {
                    return (
                      <div key={index} style={{padding: 5}}>
                        <div>
                          {item?.headings?.map((head: any, index: number) => {
                            const replaceHeading = replaceCurrentAndPrevious(
                              head?.heading,
                              selectedYear ?? ''
                            );
                            switch (head.headingType) {
                              case inputType.HEADING_5:
                                return <h5 key={index}>{replaceHeading}</h5>;
                              case inputType.HEADING_4:
                                return <h4 key={index}>{replaceHeading}</h4>;
                              case inputType.HEADING_3:
                                return <h3 key={index}>{replaceHeading}</h3>;
                              case inputType.HEADING_2:
                                return <h2 key={index}>{replaceHeading}</h2>;
                              case inputType.HEADING_1:
                                return <h1 key={index}>{replaceHeading}</h1>;
                              default:
                                return <h2 key={index}>{replaceHeading}</h2>;
                            }
                          })}
                        </div>
                        <div>
                          {item.question === '' ? (
                            <></>
                          ) : item?.tooltip ? (
                            <>
                              <b>{item.question}</b>
                              <Tooltip
                                title={item.tooltip}
                                autoAdjustOverflow={true}
                                arrow={false}
                                overlayStyle={tooltipCSS}
                              >
                                <ExclamationCircleOutlined className="iButtonIcon" />
                              </Tooltip>
                            </>
                          ) : (
                            <b>
                              {`${
                                mainItem?.showIndex === false
                                  ? ''
                                  : `${++index} .`
                              } ${item.question ?? ''}`}
                            </b>
                          )}
                        </div>
                        <div style={{marginTop: 5}}>
                          <SubmitDisabledContext.Provider
                            value={setIsSubmitDisabled}
                          >
                            {item?.field?.map(
                              (fieldParam: IField, fieldIndex: number) => {
                                return (
                                  <QuestionnaireSwitch
                                    field={{
                                      ...fieldParam,
                                      fieldLabel: replaceCurrentAndPrevious(
                                        fieldParam.fieldLabel,
                                        selectedYear ?? ''
                                      ),
                                      validationData: validationData
                                    }}
                                    key={fieldIndex}
                                  />
                                );
                              }
                            )}
                          </SubmitDisabledContext.Provider>
                        </div>
                      </div>
                    );
                  })}
                </Fragment>
              );
            })}
          </>
        )}
        <div style={{float: 'right', marginTop: 20}}>
          <Button
            type="primary"
            htmlType="submit"
            disabled={
              isSubmitDisabled ? true : isSubmitting ? !onlySubmit : false
            }
            onClick={() => {
              setOnlySubmit(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                form.submit();
                e.preventDefault();
                setOnlySubmit(true);
              }
            }}
            loading={isSubmitting ? onlySubmit : false}
          >
            Submit
          </Button>
          {nextTabId ? (
            <Button
              type="primary"
              htmlType="submit"
              style={{marginLeft: 5}}
              disabled={
                isSubmitDisabled ? true : isSubmitting ? onlySubmit : false
              }
              name="submitNext"
              onClick={() => {
                setOnlySubmit(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  form.submit();
                  e.preventDefault();
                  setOnlySubmit(false);
                }
              }}
              loading={isSubmitting ? !onlySubmit : false}
            >
              Save & next
            </Button>
          ) : (
            ''
          )}
        </div>
      </Form>
    </>
  );
};

export default Questionnaire;
